import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {fetchProfile} from '../ApiProvider/api/profile';
import {useProfile} from './ProfileProvider';
import {ConfigContext} from '../ConfigProvider';
import {supportFetch} from '../../helpers/supportFetch';
import {ApiProvider} from '../ApiProvider/ApiProvider';
import {ScreenName, useScreen} from '../ScreenProvider';

export const HandleAuthFailureContext = createContext<{
  needsAuth: boolean;
  isPollingAuth?: boolean;
  handleAuthFailure: () => void;
}>({
  needsAuth: false,
  isPollingAuth: false,
  handleAuthFailure: () => {},
});

export function useHandleAuthFailure() {
  return useContext(HandleAuthFailureContext);
}

type fetchShape = typeof fetch;

function HandleAuthFailureApiProviderWrapper({
  children,
  fetch = supportFetch,
}: {
  children: ReactNode;
  fetch?: fetchShape;
}) {
  const {profile} = useProfile();
  const {handleAuthFailure} = useHandleAuthFailure();

  const requiresAuth = profile.visitor.sessionType !== 'anonymous';

  const apiFetch = useMemo(() => {
    const wrapped: fetchShape = async (input, init?) => {
      const response = await fetch(input, init);
      if (response.status === 401 && requiresAuth) {
        handleAuthFailure();
      }
      return response;
    };

    return wrapped;
  }, [fetch, requiresAuth, handleAuthFailure]);

  return <ApiProvider fetch={apiFetch}>{children}</ApiProvider>;
}

const POLL_INTERVAL_MS = 1000;

export function HandleAuthFailureApiProvider({
  children,
}: React.PropsWithChildren) {
  const config = useContext(ConfigContext);
  const {audience, entrypoint: entryPoint, surfaceLoadId, livemode} = config;

  const {profile, setProfile} = useProfile();

  const {screen, transitionTo} = useScreen();

  const fetching = useRef<boolean>(false);

  const [needsAuth, setNeedsAuth] = useState<boolean>(false);
  const [pollForUser, setPollForUser] = useState<boolean>(false);
  const [pollAttemptNumber, setPollAttemptNumber] = useState<number>(0);

  const poll = useCallback(() => {
    if (fetching.current) {
      return;
    }

    fetching.current = true;

    fetchProfile(
      supportFetch,
      {
        audience,
        entryPoint: entryPoint ?? 'dashboard',
        livemode,
        // if we are requesting login, then session is implicitly required
        requiresSession: true,
        attemptNumber: 0,
        surfaceLoadId,
      },
      !!profile.featureFlags.irIndigoCollectFixes,
    )
      .then((data) => {
        fetching.current = false;

        if (
          'profile' in data &&
          data.profile.visitor.sessionType !== 'anonymous'
        ) {
          setProfile(data.profile);
          setPollAttemptNumber(0);
          setPollForUser(false);
          return;
        }

        setPollAttemptNumber(pollAttemptNumber + 1);
      })
      .catch(() => {
        fetching.current = false;
        setPollAttemptNumber(pollAttemptNumber + 1);
      });
  }, [
    audience,
    entryPoint,
    livemode,
    pollAttemptNumber,
    setProfile,
    surfaceLoadId,
    profile.featureFlags.irIndigoCollectFixes,
  ]);

  const handleAuthFailure = useCallback(() => {
    if (screen.name !== ScreenName.Error) {
      transitionTo({
        name: ScreenName.Error,
        props: {showInvalidSessionMessage: true},
      });
    }
    setNeedsAuth(true);
  }, [screen.name, transitionTo]);

  useEffect(() => {
    if (!pollForUser || fetching.current) {
      return;
    }

    // trigger next poll when pollAttemptNumber changes
    setTimeout(poll, POLL_INTERVAL_MS);
  }, [poll, pollAttemptNumber, pollForUser]);

  // if profile somehow gets set, stop polling
  useEffect(() => {
    if (profile.visitor.sessionType !== 'anonymous' && pollForUser) {
      setPollForUser(false);
    }
  }, [pollForUser, profile.visitor]);

  return (
    <HandleAuthFailureContext.Provider
      value={{
        handleAuthFailure,
        needsAuth,
        isPollingAuth: pollForUser,
      }}
    >
      <HandleAuthFailureApiProviderWrapper>
        {children}
      </HandleAuthFailureApiProviderWrapper>
    </HandleAuthFailureContext.Provider>
  );
}
